import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Form, FormikProvider, useFormik } from 'formik';
import {
  Stack,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormHelperText,
  TextareaAutosize,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';

import { useStyles } from '../styles/projectSettingsFormStyles';
import { INITIAL_PROJECT_FORM_STATE } from '../../constants';
import ProjectSchema from '../../schema/projectSchema';
import { PROJECT_STATUS_LIST } from '../../../../Constants/ProjectConstants';
import { getAllClients } from '../../../../services/clientService';

const label = { inputProps: { 'aria-label': 'Checkbox demo' } };

const ProjectSettingsForm = ({ isEditable, projectDetails, onSubmit, children }) => {
  const classes = useStyles();
  const [clientList, setClientList] = useState([]);

  const handleSubmit = async (values, { setSubmitting }) => {
    setSubmitting(true);
    await onSubmit(values);
    setSubmitting(false);
  };

  const formik = useFormik({
    initialValues: INITIAL_PROJECT_FORM_STATE,
    validationSchema: ProjectSchema,
    onSubmit: handleSubmit,
  });

  const { setValues, values, touched, errors, getFieldProps, setFieldValue, submitForm, isSubmitting } = formik;

  useEffect(() => {
    setValues({
      projectName: projectDetails?.projectName,
      clientId: projectDetails?.clientId,
      totalHrs: projectDetails?.totalHrs,
      status: projectDetails?.status,
      allowOverSpent: projectDetails?.allowOverSpent,
      allowManualTask: projectDetails?.allowManualTask,
      description: projectDetails?.description ?? '',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectDetails]);

  useEffect(() => {
    const fetchClientsData = async () => {
      const response = await getAllClients();
      setClientList(response?.data?.data?.rows);
    };

    fetchClientsData();
  }, []);
  return (
    <FormikProvider value={formik}>
      <Form onSubmit={formik?.handleSubmit}>
        <Stack spacing={2}>
          <div className={`${classes.inputDiv} ${classes.container}`}>
            <label htmlFor="projectName" className={classes.inputLabel}>
              Project Name :{' '}
            </label>
            <TextField
              disabled={!isEditable}
              label="Project Name"
              {...getFieldProps('projectName')}
              error={touched.projectName && Boolean(errors.projectName)}
              helperText={touched.projectName && errors.projectName}
              fullWidth
            />
          </div>
          <div className={`${classes.inputDiv} ${classes.container}`}>
            <label htmlFor="clientId" className={classes.inputLabel}>
              Client :{' '}
            </label>
            <FormControl fullWidth>
              <InputLabel>Client</InputLabel>
              <Select
                disabled={!isEditable}
                {...getFieldProps('clientId')}
                error={touched.clientId && Boolean(errors.clientId)}
                helperText={touched.clientId && errors.clientId}
                // defaultValue={action === 'update' ? 'Inhouse' : ''}
                label="Client"
                onChange={(event, value) => {
                  setFieldValue('clientId', value?.props?.value);
                }}
              >
                {clientList.map((client, i) => (
                  <MenuItem value={client.id} key={i}>
                    {client.clientName}
                  </MenuItem>
                ))}
              </Select>
              {touched.clientId && errors.clientId && (
                <FormHelperText className={classes.helperText}>{errors.clientId}</FormHelperText>
              )}
            </FormControl>
          </div>
          <div className={`${classes.inputDiv} ${classes.container}`}>
            <label htmlFor="status" className={classes.inputLabel}>
              Project Status :{' '}
            </label>
            <FormControl fullWidth>
              <InputLabel>Project Status</InputLabel>
              <Select
                {...getFieldProps('status')}
                disabled={!isEditable}
                error={touched.status && Boolean(errors.status)}
                helperText={touched.status && errors.status}
                label="Project Status"
                onChange={(event, value) => {
                  setFieldValue('status', value?.props?.value);
                }}
              >
                {PROJECT_STATUS_LIST.sort().map((list) => (
                  <MenuItem sx={{ textTransform: 'capitalize' }} value={list} key={list}>
                    {list}
                  </MenuItem>
                ))}
              </Select>
              {touched.status && errors.status && (
                <FormHelperText className={classes.helperText}>{errors.status}</FormHelperText>
              )}
            </FormControl>
          </div>
          <div className={`${classes.inputDiv} ${classes.container}`}>
            <label htmlFor="totalHrs" className={classes.inputLabel}>
              Project Budget Hours :{' '}
            </label>
            <TextField
              disabled={!isEditable}
              label="Project Budget Hours"
              type="text"
              inputMode="numeric"
              // pattern="[0-9]*"
              value={values.totalHrs}
              onChange={(e) => {
                const inputValue = e.target.value;
                const isValidInput = /^-?\d*\.?\d*$/.test(inputValue); // Check if input matches the pattern for valid decimal/float values
                if (isValidInput && inputValue >= 0) {
                  // Check if input is valid and not negative
                  setFieldValue('totalHrs', inputValue);
                }
              }}
              error={touched.totalHrs && Boolean(errors.totalHrs)}
              helperText={touched.totalHrs && errors.totalHrs}
              fullWidth
              autoComplete="off"
            />
          </div>
          {(projectDetails.description || isEditable) && (
            <FormControl fullWidth sx={{ gap: 2 }}>
              <span htmlFor="description">Description :</span>
              <TextareaAutosize
                disabled={!isEditable}
                placeholder="Write project description here..."
                minRows={3}
                {...getFieldProps('description')}
                error={touched.description && Boolean(errors.description)}
                helperText={touched.description && errors.description}
                className={classes.projectDescription}
              />
              {touched.description && errors.description && (
                <FormHelperText className={classes.helperText}>{errors.description}</FormHelperText>
              )}
            </FormControl>
          )}

          <div className={classes.container}>
            <Checkbox
              disabled={!isEditable}
              checked={values?.allowOverSpent}
              {...label}
              onChange={(event, check) => {
                setFieldValue('allowOverSpent', check);
              }}
            />
            <Typography className={classes.checkBoxText}>Allow Excess Hours</Typography>
            <Checkbox
              disabled={!isEditable}
              checked={values?.allowManualTask}
              {...label}
              onChange={(event, check) => {
                setFieldValue('allowManualTask', check);
              }}
            />{' '}
            <Typography className={classes.checkBoxText}>Allow Manual Task Entry</Typography>
          </div>

          {children}

          {isEditable && (
            <LoadingButton
              fullWidth
              size="large"
              variant="contained"
              onClick={submitForm}
              loading={isSubmitting}
              className={classes.submitButton}
            >
              Save
            </LoadingButton>
          )}
        </Stack>
      </Form>
    </FormikProvider>
  );
};

ProjectSettingsForm.propTypes = {
  isEditable: PropTypes.bool,
  projectDetails: PropTypes.object,
  onSubmit: PropTypes.func,
  children: PropTypes.node,
};

export default ProjectSettingsForm;
